import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, Tag, X, ImagePlus, FileText } from "lucide-react";
import api from "../../lib/api";

/**
 * AttachmentsPanel — reusable photo/document uploader for every Safe Vault section.
 * Every attachment is stored in `sv_attachments` (isolated from community photos).
 * Rendering images: axios can't attach an Auth header via <img src>, so we fetch
 * each file as a blob and swap in an object URL.
 *
 * Props:
 *   parentType: "medication" | "allergy" | "vital" | "emergency" | "other"
 *   parentId:   optional string
 *   defaultTags: array of tag strings suggested at upload time
 *   compact:    boolean — condensed layout for inline use
 */

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif,application/pdf";

function BlobImg({ id, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    api.get(`/vault/health/attachments/${id}/file`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const b = new Blob([r.data], { type: r.headers["content-type"] || "image/jpeg" });
        const u = URL.createObjectURL(b);
        revoke = u;
        setSrc(u);
      })
      .catch(() => { if (!cancelled) setSrc(""); });
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [id]);
  if (src === null) return <div className={`${className} bg-zinc-100 grid place-items-center`}><Loader2 className="w-4 h-4 animate-spin text-zinc-400" /></div>;
  if (src === "") return <div className={`${className} bg-zinc-100 grid place-items-center text-[10px] text-muted-foreground`}>failed</div>;
  return <img src={src} alt={alt || ""} className={className} loading="lazy" />;
}

export default function AttachmentsPanel({ parentType, parentId, defaultTags = [], compact = false }) {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(null); // full-screen viewer for a photo id

  const load = useCallback(async () => {
    const params = { parent_type: parentType };
    if (parentId) params.parent_id = parentId;
    try {
      const r = await api.get("/vault/health/attachments", { params });
      setItems(r.data.items || []);
    } catch { setItems([]); }
  }, [parentType, parentId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (files, tagsInput) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: max 15 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("parent_type", parentType);
      if (parentId) fd.append("parent_id", parentId);
      const tags = [...defaultTags, ...(tagsInput || "").split(",").map((s) => s.trim()).filter(Boolean)];
      if (tags.length) fd.append("tags", tags.join(","));
      try {
        await api.post("/vault/health/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(e?.response?.data?.detail || `${f.name}: upload failed`);
      }
    }
    if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} added`);
    setUploading(false);
    load();
  };

  const remove = async (a) => {
    if (!window.confirm("Remove this photo? It will be soft-deleted — original is not recoverable from the UI.")) return;
    try { await api.delete(`/vault/health/attachments/${a.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const editTags = async (a) => {
    const current = (a.tags || []).join(", ");
    const val = window.prompt("Tags (comma-separated, letters/digits/space only):", current);
    if (val === null || val === current) return;
    try {
      await api.patch(`/vault/health/attachments/${a.id}`, { tags: val.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Tags updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className={`border border-zinc-200 bg-white ${compact ? "" : "p-3"}`} data-testid={`attachments-${parentType}${parentId ? `-${parentId}` : ""}`}>
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2 text-sm font-700">
          <Camera className="w-4 h-4 text-emerald-600" />
          Photos &amp; docs
          {items && <span className="text-[11px] font-500 text-muted-foreground">· {items.length}</span>}
        </div>
        <label className="cursor-pointer inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-xs font-700 hover:bg-emerald-700" data-testid={`attachments-upload-${parentType}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
          Add
          <input type="file" accept={ACCEPT} multiple hidden onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; upload(fs); }} />
        </label>
      </div>

      {items === null ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground px-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground px-1">No photos yet. Tap <b>Add</b> to attach a pill photo, receipt, prescription snippet or PDF (max 15 MB each). Tags let you cross-reference them later.</p>
      ) : (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
          {items.map((a) => {
            const isPdf = (a.content_type || "").includes("pdf");
            return (
              <div key={a.id} className="relative group border border-zinc-200 bg-zinc-50" data-testid={`attachment-${a.id}`}>
                <button onClick={() => !isPdf && setExpanded(a)} className="block w-full aspect-square overflow-hidden">
                  {isPdf ? (
                    <div className="w-full h-full grid place-items-center text-xs text-zinc-600 gap-1"><FileText className="w-6 h-6 text-rose-500" /> PDF</div>
                  ) : (
                    <BlobImg id={a.id} alt={a.caption} className="w-full h-full object-cover" />
                  )}
                </button>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1 flex items-end justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => editTags(a)} title="Edit tags" className="p-1 text-white/90 hover:text-white" data-testid={`attachment-tags-${a.id}`}><Tag className="w-3 h-3" /></button>
                  <button onClick={() => remove(a)} title="Remove" className="p-1 text-white/90 hover:text-rose-300" data-testid={`attachment-delete-${a.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
                {(a.tags || []).length > 0 && (
                  <div className="absolute top-1 left-1 flex flex-wrap gap-0.5 max-w-full">
                    {(a.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="text-[9px] font-800 bg-emerald-600/90 text-white px-1 py-0.5 uppercase tracking-wider">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {expanded && (
        <div className="fixed inset-0 z-[9999] bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setExpanded(null)} data-testid="attachment-viewer">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={() => setExpanded(null)}><X className="w-6 h-6" /></button>
          <div className="max-w-4xl w-full max-h-[90vh] flex flex-col items-center gap-3">
            <BlobImg id={expanded.id} alt={expanded.caption} className="max-h-[80vh] w-auto object-contain" />
            <div className="text-white/80 text-xs text-center">
              {expanded.caption || <em className="text-white/40">no caption</em>}
              {(expanded.tags || []).length > 0 && <span className="ml-2">· {(expanded.tags || []).map((t) => `#${t}`).join(" ")}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

